/*
 * -----------------------------------------------------------------------
 *  <copyright file="heatmap/display.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import HeatMapChart from 'views/components/core/charts/heat-map-chart';

const getHeatmap = ({ getRef, panelState, siteId, sites, subjects, setLoadingComplete }: any) => {
    if (!panelState?.panelData?.panelData?.length || !panelState.panelData.panelData[0].heatMapForStudy) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>No data</span>;
    }

    const heatMapData = (
        (siteId && panelState.panelData.panelData[0].heatMapForSiteIds
            ? panelState.panelData.panelData[0].heatMapForSiteIds[siteId]
            : panelState.panelData.panelData[0].heatMapForStudy ?? []) ?? []
    ).filter(
        (item: any) =>
            !sites || !subjects || !!(panelState.chartConfig.displayBy === 'SITE' ? sites[item.y] : subjects[item.y]),
    );

    const xValues: string[] = Array.from(new Set(heatMapData.map((p: any): string => p.x)));

    // Sort the y values first before generating the categories
    const yValues: any[] = Array.from(new Set(heatMapData.map((p: any): string => p.y))).sort((a: any, b: any) => {
        const val1 =
            panelState.chartConfig.displayBy === 'SITE'
                ? sites[a]?.Fields['Site.name']
                : subjects[a]?.Fields['Subject.subjectNumber'];
        const val2 =
            panelState.chartConfig.displayBy === 'SITE'
                ? sites[b]?.Fields['Site.name']
                : subjects[b]?.Fields['Subject.subjectNumber'];

        return !isNaN(+val1) && !isNaN(+val2) ? +val1 - +val2 : val1.localeCompare(val2);
    });

    const yCats =
        panelState.chartConfig.displayBy === 'SITE'
            ? yValues.map((steId: string) => sites[steId]?.Fields['Site.name'])
            : yValues.map((sbjId: string) => subjects[sbjId]?.Fields['Subject.subjectNumber']);

    const getData = () => {
        // Create a value Map
        // i.e valueMap now looks like:
        // {
        //   A: { '1': 10, '2': 20 },
        //   B: { '1': 30 }
        // }
        const valueMap: Record<string, Record<string, number>> = {};
        (heatMapData ?? []).forEach((obj: any) => {
            if (!valueMap[obj.x]) valueMap[obj.x] = {};
            valueMap[obj.x][obj.y] = obj.value;
        });
        // We loop through each x-axis values for each y-axis value
        // getting the "Value" from the valueMap object
        // We need to do it like this so that we don't lose any missing records
        // and avoid incorrect data, if the heatMapData was not in order
        const array = [];
        for (let i = 0; i < xValues.length; i++) {
            const xValue = valueMap[xValues[i]];
            for (let j = 0; j < yValues.length; j++) {
                array.push(xValue?.[yValues[j]] ?? 0);
            }
        }
        return array;
    };

    return (
        <HeatMapChart
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            xAxis={{ name: panelState.chartConfig.xTitle, categories: xValues }}
            yAxis={{ name: panelState.chartConfig.yTitle, categories: yCats }}
            series={{
                name: panelState.chartConfig.plotTitle,
                data: getData(),
            }}
            hidePlotTitle={panelState.hidePlotTitle}
        />
    );
};

export { getHeatmap };
